import React, { useMemo } from "react";
import type { MvpVotingStatus, MvpTally, Teammate } from "../../hooks/data-fetching/useMvpVoting";

// =============================================================================
// MVP ADMIN MODAL — Exec/Manager view of voting tallies
// =============================================================================
// Shows: participation stats, anonymous ranked tallies with vote bars, month context.
// Voter identities NOT shown — anonymous per spec (2026-04-28).
// Only accessible to canManageTeam roles (exec, manager, client).
// =============================================================================

interface MvpAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  votingStatus: MvpVotingStatus | null;
  teammates: Teammate[];
  monthLabel: string;
}

const MvpAdminModal: React.FC<MvpAdminModalProps> = ({
  isOpen,
  onClose,
  votingStatus,
  teammates: _teammates, // prop kept for caller compat; not used after anonymity refactor
  monthLabel,
}) => {
  const tallies = votingStatus?.tallies || [];

  const maxVotes = useMemo(() => {
    if (!tallies.length) return 1;
    return Math.max(...tallies.map((t) => t.votes), 1);
  }, [tallies]);

  if (!isOpen || !votingStatus) return null;

  const participationPct =
    votingStatus.total_eligible > 0
      ? Math.round((votingStatus.total_voted / votingStatus.total_eligible) * 100)
      : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      <div
        className="relative liquid-glass rounded-[20px] w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3 border-b border-black/[0.06]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="token-18h text-glass">MVP Voting — Admin</h2>
              <p className="token-11 text-glass-muted mt-0.5">{monthLabel}</p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap w-7 h-7 flex items-center justify-center rounded-lg hover:bg-black/[0.06] transition-colors text-glass-muted"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="detail-overlay overflow-y-auto flex-1 p-5 space-y-5">
          {/* Participation Stats */}
          <div className="liquid-glass-light anim-lift rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="token-11u text-glass-muted">Participation</p>
                <p className="token-24m text-glass mt-1">
                  {votingStatus.total_voted}
                  <span className="token-15 text-glass-muted">
                    {" "}
                    / {votingStatus.total_eligible}
                  </span>
                </p>
              </div>
              <div className="text-right">
                <p
                  className={`token-18m ${participationPct >= 80 ? "text-ds-green" : participationPct >= 50 ? "text-ds-amber" : "text-ds-red"}`}
                >
                  {participationPct}%
                </p>
                <p className="token-10 text-glass-muted">voted</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-2 rounded-full bg-black/[0.05] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  participationPct >= 80
                    ? "bg-ds-green"
                    : participationPct >= 50
                      ? "bg-ds-amber"
                      : "bg-ds-red"
                }`}
                style={{ width: `${participationPct}%` }}
              />
            </div>
          </div>

          {/* Tallies — ranked with score bars */}
          <div>
            <h3 className="token-14s text-glass mb-3">Vote Tallies</h3>
            {votingStatus.tallies.length === 0 ? (
              <p className="token-13 text-glass-muted py-4 text-center">No votes yet.</p>
            ) : (
              <div className="space-y-2">
                {votingStatus.tallies.map((tally: MvpTally, idx: number) => {
                  const barWidth = Math.max(8, (tally.votes / maxVotes) * 100);
                  return (
                    <div key={tally.nominee_id} className="flex items-center gap-3">
                      <span className="token-13m text-glass-muted w-5 text-right flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="token-13 text-glass truncate">{tally.nominee_name}</p>
                          <span className="token-13m text-ds-accent flex-shrink-0 ml-2">
                            {tally.votes} vote{tally.votes !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-black/[0.05] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-ds-accent transition-all"
                            style={{ width: `${barWidth}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Voter Breakdown and Haven't Voted sections removed 2026-04-28.
              Voter identities are anonymous per spec — no one can see who voted for who.
              High-participation "Haven't Voted" could reverse-identify non-voters. */}
        </div>
      </div>
    </div>
  );
};

export default MvpAdminModal;
